import { forwardRef, ReactElement, Ref } from "react";
import { Box } from "@twilio-paste/core/box";
import { HelpText } from "@twilio-paste/core/help-text";
import { Label } from "@twilio-paste/core/label";
import { Text } from "@twilio-paste/core/text";
import { TextArea } from "@twilio-paste/core/textarea";

import { constants, types } from "./definitions";

const CompositeTextArea = forwardRef(
  (
    {
      errorText,
      hasHtmlInLabel,
      helpText,
      id,
      isMarkdownAccepted = false,
      label,
      // Paste recommendation: https://paste.twilio.design/components/textarea/#positioning-form-fields
      marginBottom = "space80",
      onChange,
      required,
      value,
      width,
      ...rest
    }: types.CompositeTextAreaProps,
    ref: Ref<HTMLTextAreaElement>
  ): ReactElement => {
    const renderMarkdownInfo = (): ReactElement => {
      const markdownInfo = [
        <b key="bold">**bold**</b>,
        <i key="italics">_italics_</i>,
        <>
          ~~ <del>strike</del> ~~
        </>,
        <code key="formatted">`formatted`</code>,
        <code key="code">```code```</code>,
      ];

      return (
        <Box as="div">
          {markdownInfo.map((info, index) => (
            <Text
              key={index}
              fontSize="fontSize20"
              as="span"
              paddingRight="space40"
            >
              {info}
            </Text>
          ))}
        </Box>
      );
    };

    const errorTextId =
      rest.hasError && rest.name
        ? `${rest.name}${constants.ERROR_TEXT_ID_TAG}`
        : "";
    const helpTextId =
      helpText && rest.name ? `${rest.name}${constants.HELP_TEXT_ID_TAG}` : "";

    const getDefaultErrorText = (): string =>
      `Please enter a ${rest?.name || "value"}`;

    return (
      <Box marginBottom={marginBottom} width={width}>
        <Label htmlFor={id} required={required}>
          {hasHtmlInLabel ? (
            <span dangerouslySetInnerHTML={{ __html: label }} />
          ) : (
            label
          )}
        </Label>

        <TextArea
          aria-describedby={`${errorTextId} ${errorTextId}`}
          id={id}
          onChange={onChange}
          value={value}
          ref={ref}
          {...rest}
        />

        {helpText && !rest.hasError && (
          <HelpText id={helpTextId}>{helpText}</HelpText>
        )}

        {rest.hasError && (
          <HelpText id={errorTextId} variant="error">
            {errorText || getDefaultErrorText()}
          </HelpText>
        )}

        {isMarkdownAccepted && renderMarkdownInfo()}
      </Box>
    );
  }
);

export default CompositeTextArea;
